import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { api } from "../api";
import { scheduleAndroidTimeline } from "../notifications";
import { syncAndroidSchedules } from "../nativeSchedule";
import { formatDate, messageDisplayText, readableError, toIso } from "../composables/format";
import { useToast } from "../composables/useToast";
import { useAuthStore } from "./auth";
import { useChannelStore } from "./channels";
import { loadOfflineSnapshot, saveOfflineSnapshot } from "../offlineSnapshot";
import type { Attachment, Message, MessageDetail, MessageDetailActionOptions, Project, ProjectDetail, Reminder, Schedule, ScheduleCreatePayload, SearchPayload, TodayPayload, Todo } from "../types";

type ChannelCache = {
  messages: Message[];
  attachments: Attachment[];
  todos: Todo[];
  reminders: Reminder[];
  schedules: Schedule[];
  projects: Project[];
  lastSyncAt?: string;
};

export type CaptureKind = "record" | "todo" | "reminder";

export type SendMessageOptions = {
  kind?: CaptureKind;
  remindAt?: string;
};

export const useDataStore = defineStore("data", () => {
  const toast = useToast();
  const authStore = useAuthStore();
  const channelStore = useChannelStore();
  const messages = ref<Message[]>([]);
  const attachments = ref<Attachment[]>([]);
  const todos = ref<Todo[]>([]);
  const reminders = ref<Reminder[]>([]);
  const schedules = ref<Schedule[]>([]);
  const projects = ref<Project[]>([]);
  const todayData = ref<TodayPayload | null>(null);
  const searchResults = ref<SearchPayload | null>(null);
  const lastSearchQuery = ref("");
  const activeProject = ref<ProjectDetail | null>(null);
  const syncStatus = ref("未同步");
  const lastSyncAt = ref("");
  const lastSyncCount = ref(0);
  const lastSyncError = ref("");
  const loading = ref(false);
  const detail = ref<MessageDetail | null>(null);
  const onlyCurrentChannel = ref(false);
  const uploadedAttachments = ref<Attachment[]>([]);
  const channelCache = ref<Record<string, ChannelCache>>({});
  const offlineReadOnly = ref(false);
  const needsSync = ref(false);

  const searchResultCount = computed(() => {
    if (!searchResults.value) return 0;
    return searchResults.value.messages.length + searchResults.value.todos.length + searchResults.value.reminders.length + searchResults.value.schedules.length;
  });
  const pendingMessages = computed(() => messages.value.filter((message) =>
    message.author_id === authStore.user?.id
      && (message.status === "pending_confirmation" || message.ai_needs_confirmation)));
  const pendingCount = computed(() => pendingMessages.value.length);

  async function refreshAll(showStatus = true) {
    loading.value = true;
    try {
      if (channelStore.channels.length === 0) await channelStore.loadChannels();
      const current = channelStore.currentChannel;
      const cached = current ? channelCache.value[current.id] : null;
      const snapshot = await api.sync(current ? { channels: [current.id], since: cached?.lastSyncAt } : {});
      channelStore.replaceChannels(snapshot.channels);
      if (current) {
        messages.value = activeRows(cached?.lastSyncAt ? mergeById(cached.messages, snapshot.messages, "created_at") : snapshot.messages);
        attachments.value = activeRows(cached?.lastSyncAt ? mergeById(cached.attachments, snapshot.attachments || [], "updated_at") : (snapshot.attachments || []));
        todos.value = activeRows(cached?.lastSyncAt ? mergeById(cached.todos, snapshot.todos, "updated_at") : snapshot.todos);
        reminders.value = activeRows(cached?.lastSyncAt ? mergeById(cached.reminders, snapshot.reminders, "updated_at") : snapshot.reminders);
        schedules.value = activeRows(cached?.lastSyncAt ? mergeById(cached.schedules, snapshot.schedules || [], "updated_at") : (snapshot.schedules || []));
        projects.value = activeRows(cached?.lastSyncAt ? mergeById(cached.projects, snapshot.projects, "updated_at") : snapshot.projects);
        channelCache.value[current.id] = {
          messages: messages.value,
          attachments: attachments.value,
          todos: todos.value,
          reminders: reminders.value,
          schedules: schedules.value,
          projects: projects.value,
          lastSyncAt: snapshot.server_time
        };
      } else {
        messages.value = activeRows(snapshot.messages);
        attachments.value = activeRows(snapshot.attachments || []);
        todos.value = activeRows(snapshot.todos);
        reminders.value = activeRows(snapshot.reminders);
        schedules.value = activeRows(snapshot.schedules || []);
        projects.value = activeRows(snapshot.projects);
      }
      syncStatus.value = `已同步 ${formatDate(snapshot.server_time)}`;
      offlineReadOnly.value = false;
      needsSync.value = false;
      lastSyncAt.value = snapshot.server_time;
      lastSyncCount.value = snapshot.messages.length + snapshot.todos.length + snapshot.reminders.length
        + (snapshot.schedules?.length || 0) + snapshot.projects.length + (snapshot.attachments?.length || 0);
      lastSyncError.value = "";
      scheduleAndroidTimeline(reminders.value, schedules.value, channelStore.mutedChannelIds).catch(() => undefined);
      syncAndroidSchedules(schedules.value).then(applyNativeScheduleUpdates).catch(() => undefined);
      if (current && authStore.user) {
        saveOfflineSnapshot({ userId: authStore.user.id, channelId: current.id, savedAt: Date.now(), channels: channelStore.channels, messages: messages.value, attachments: attachments.value, todos: todos.value, reminders: reminders.value, schedules: schedules.value, projects: projects.value, lastSyncAt: snapshot.server_time }).catch(() => undefined);
      }
    } catch (error) {
      lastSyncError.value = error instanceof Error ? error.message : String(error);
      if (showStatus) syncStatus.value = `同步失败：${lastSyncError.value}`;
      throw error;
    } finally {
      loading.value = false;
    }
  }

  function clearLocal() {
    messages.value = [];
    attachments.value = [];
    todos.value = [];
    reminders.value = [];
    schedules.value = [];
    projects.value = [];
    todayData.value = null;
    searchResults.value = null;
    lastSearchQuery.value = "";
    activeProject.value = null;
    detail.value = null;
    uploadedAttachments.value = [];
    channelCache.value = {};
    syncStatus.value = "未同步";
    lastSyncAt.value = "";
    lastSyncCount.value = 0;
    lastSyncError.value = "";
    offlineReadOnly.value = false;
    needsSync.value = false;
  }

  async function restoreOfflineSnapshot(channelId?: string) {
    const userId = authStore.user?.id;
    const id = channelId || channelStore.currentChannel?.id;
    if (!userId || !id) return false;
    const snapshot = await loadOfflineSnapshot(userId, id).catch(() => null);
    if (!snapshot) return false;
    channelStore.channels = snapshot.channels;
    messages.value = snapshot.messages;
    attachments.value = snapshot.attachments;
    todos.value = snapshot.todos;
    reminders.value = snapshot.reminders;
    schedules.value = snapshot.schedules;
    projects.value = snapshot.projects;
    channelCache.value[id] = { messages: snapshot.messages, attachments: snapshot.attachments, todos: snapshot.todos, reminders: snapshot.reminders, schedules: snapshot.schedules, projects: snapshot.projects, lastSyncAt: snapshot.lastSyncAt };
    lastSyncAt.value = snapshot.lastSyncAt || "";
    offlineReadOnly.value = true;
    syncStatus.value = "离线只读：显示最近同步快照";
    return true;
  }

  function applyChannelCache(channelId: string) {
    const cached = channelCache.value[channelId];
    if (!cached) return false;
    messages.value = cached.messages;
    attachments.value = cached.attachments;
    todos.value = cached.todos;
    reminders.value = cached.reminders;
    schedules.value = cached.schedules;
    projects.value = cached.projects;
    activeProject.value = null;
    detail.value = null;
    return true;
  }

  async function sendMessage(content: string, options: SendMessageOptions = {}) {
    const kind = options.kind || "record";
    if (kind === "reminder" && !options.remindAt) throw new Error("请先选择提醒时间。");
    if (offlineReadOnly.value) throw new Error("离线只读状态下不能新建记录");
    try {
      const created = await api.createMessage(
        content,
        kind !== "record",
        channelStore.currentChannel?.id,
        uploadedAttachments.value.map((item) => item.id)
      );
      uploadedAttachments.value = [];
      let conversion: "none" | "success" | "failed" = "none";
      if (kind !== "record") {
        try {
          await api.confirmMessage(created.message.id, {
            action: kind,
            title: messageDisplayText(created.message, created.attachments),
            priority: kind === "todo" ? "normal" : undefined,
            remind_at: kind === "reminder" ? toIso(options.remindAt!) : undefined
          });
          conversion = "success";
        } catch (error) {
          conversion = "failed";
          toast.error(`原文已保存，但转为${kind === "todo" ? "待办" : "提醒"}失败：${readableError(error)}`);
        }
      }
      needsSync.value = true;
      await refreshAll(false).catch((error) => {
        toast.error(`原文已保存，但刷新失败：${readableError(error)}`);
      });
      if (kind !== "record") await loadToday().catch(() => undefined);
      return { ...created, conversion };
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function undoMessage(id: string) {
    try {
      await api.deleteMessage(id);
      await refreshAll(false);
      await loadToday();
      toast.success("已撤销发送");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function openMessage(id: string) {
    detail.value = await api.messageDetail(id);
    const recentKey = `mindback.recentViews.${authStore.user?.id || "anonymous"}`;
    const recentIds = (() => {
      try {
        return JSON.parse(localStorage.getItem(recentKey) || "[]") as string[];
      } catch {
        return [];
      }
    })();
    localStorage.setItem(recentKey, JSON.stringify([id, ...recentIds.filter((item) => item !== id)].slice(0, 20)));
  }

  function closeMessage() {
    detail.value = null;
  }

  async function saveDetail(payload: Pick<Message, "display_name" | "content" | "organizing_note">) {
    if (!detail.value) return;
    const response = await api.patchMessage(detail.value.message.id, payload);
    detail.value = { ...detail.value, message: response.message };
    messages.value = replaceById(messages.value, response.message);
    if (searchResults.value) {
      searchResults.value = {
        ...searchResults.value,
        messages: replaceById(searchResults.value.messages, response.message)
      };
    }
    const cached = channelCache.value[response.message.channel_id];
    if (cached) cached.messages = replaceById(cached.messages, response.message);
    return response.message;
  }

  async function detailAction(action: string, options: MessageDetailActionOptions = {}) {
    if (!detail.value) return;
    const payload: { action: string; title?: string; due_at?: string; priority?: Todo["priority"]; remind_at?: string; end_at?: string; all_day?: boolean; remind_minutes_before?: number; alarm_enabled?: boolean } = {
      action,
      title: messageDisplayText(detail.value.message, detail.value.attachments)
    };
    if (action === "todo") {
      if (options.todoDueAt) payload.due_at = toIso(options.todoDueAt);
      payload.priority = options.todoPriority || "normal";
    }
    if (action === "reminder") {
      if (!options.reminderAt) {
        toast.error("请先选择提醒时间。");
        throw new Error("请先选择提醒时间。");
      }
      payload.remind_at = toIso(options.reminderAt);
    }
    if (action === "schedule") {
      if (!options.scheduleStartAt) {
        toast.error("请先选择日程开始时间。");
        throw new Error("请先选择日程开始时间。");
      }
      payload.remind_at = toIso(options.scheduleStartAt);
      if (options.scheduleEndAt) payload.end_at = toIso(options.scheduleEndAt);
      payload.remind_minutes_before = 15;
      payload.alarm_enabled = options.scheduleAlarmEnabled || false;
    }
    try {
      await api.confirmMessage(detail.value.message.id, payload);
      await openMessage(detail.value.message.id);
      await refreshAll(false);
      await loadToday();
      toast.success("处理完成");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function attachDetailProject(projectId: string) {
    if (!detail.value || !projectId) return;
    try {
      await api.addProjectMessage(projectId, detail.value.message.id);
      await openMessage(detail.value.message.id);
      await refreshAll(false);
      toast.success("已关联项目");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function removeProjectMessage(projectId: string) {
    if (!detail.value) return;
    try {
      await api.removeProjectMessage(projectId, detail.value.message.id);
      await openMessage(detail.value.message.id);
      await refreshAll(false);
      if (activeProject.value?.project.id === projectId) await openProject(projectId);
      toast.success("已移除项目关联");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function deleteAi() {
    if (!detail.value) return;
    try {
      await api.deleteAi(detail.value.message.id);
      await openMessage(detail.value.message.id);
      await refreshAll(false);
      toast.success("AI 派生结果已删除");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function retryAi() {
    if (!detail.value) return;
    await api.retryAi(detail.value.message.id);
    await openMessage(detail.value.message.id);
    await refreshAll(false);
    toast.success("已重新加入自动整理队列");
  }

  async function deleteCurrentMessage() {
    if (!detail.value) return;
    try {
      await api.deleteMessage(detail.value.message.id);
      detail.value = null;
      await refreshAll(false);
      await loadToday();
      toast.success("原始消息已删除");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function loadToday() {
    todayData.value = await api.today(onlyCurrentChannel.value ? channelStore.currentChannel?.id : undefined);
  }

  async function patchTodo(id: string, payload: Partial<Todo>) {
    try {
      await api.patchTodo(id, payload);
      await refreshAll(false);
      await loadToday();
      toast.success("待办已更新");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function createTodo(title: string, dueAt?: string, priority: Todo["priority"] = "normal") {
    try {
      await api.createTodo({ title, due_at: dueAt ? toIso(dueAt) : undefined, priority });
      await refreshAll(false);
      await loadToday();
      toast.success("待办已创建");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function patchReminder(id: string, payload: Partial<Reminder> & { snooze_minutes?: number }) {
    try {
      await api.patchReminder(id, payload);
      await refreshAll(false);
      await loadToday();
      toast.success("提醒已更新");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function createManualReminder(title: string, remindAt: string) {
    try {
      await api.createReminder({ title, remind_at: toIso(remindAt), kind: "manual" });
      await refreshAll(false);
      await loadToday();
      toast.success("提醒已创建");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function createSchedule(payload: ScheduleCreatePayload) {
    try {
      const response = await api.createSchedule(payload);
      await syncOneSchedule(response.schedule);
      await refreshAll(false);
      await loadToday();
      toast.success("日程已创建");
      return response.schedule;
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function patchSchedule(id: string, payload: Partial<Schedule>) {
    try {
      const response = await api.patchSchedule(id, payload);
      await syncOneSchedule(response.schedule);
      await refreshAll(false);
      await loadToday();
      toast.success("日程已更新");
      return response.schedule;
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function deleteSchedule(id: string) {
    const existing = schedules.value.find((item) => item.id === id);
    try {
      await api.deleteSchedule(id);
      if (existing) await syncAndroidSchedules([{ ...existing, status: "deleted" }]).catch(() => []);
      await refreshAll(false);
      await loadToday();
      toast.success("日程已删除");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function runSearch(query: string, filters: {
    channel?: string;
    from_date?: string;
    to_date?: string;
    types?: string[];
    attachment_type?: string;
    project_id?: string;
  } = {}) {
    lastSearchQuery.value = query;
    try {
      searchResults.value = await api.search({
        q: query,
        channel: filters.channel ?? (onlyCurrentChannel.value ? channelStore.currentChannel?.id : undefined),
        ...filters
      });
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function reloadCurrentView(routeName: string) {
    if (routeName === "today") {
      await loadToday();
    } else if (routeName === "memory" && searchResults.value && lastSearchQuery.value) {
      await runSearch(lastSearchQuery.value);
    }
  }

  async function createProject(name: string) {
    try {
      const created = await api.createProject(name, "", channelStore.currentChannel?.id);
      await refreshAll(false);
      await openProject(created.project.id);
      toast.success("项目已创建");
      return created.project;
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function openProject(id: string) {
    activeProject.value = await api.project(id);
  }

  async function patchProject(id: string, payload: Partial<Project>) {
    try {
      const response = await api.patchProject(id, payload);
      await refreshAll(false);
      await openProject(response.project.id);
      toast.success("项目已保存");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function deleteProject(id: string) {
    try {
      await api.deleteProject(id);
      activeProject.value = null;
      await refreshAll(false);
      toast.success("项目已删除，原始消息保留");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function downloadExport(format: "json" | "md" | "csv") {
    try {
      const data = await api.exportData(format);
      const text = typeof data === "string" ? data : JSON.stringify(data, null, 2);
      const blob = new Blob([text], { type: format === "json" ? "application/json" : "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `mindback-export.${format}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("导出已开始");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function clearAccountData() {
    try {
      await api.deleteAccountData();
      await refreshAll(false);
      await loadToday();
      toast.success("账号数据已清空");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function uploadFiles(
    files: FileList | File[],
    onProgress?: (completed: number, total: number) => void
  ) {
    const list = Array.from(files);
    const uploaded: Array<{ attachment: Attachment; file: File }> = [];
    const failed: Array<{ file: File; error: string }> = [];
    for (const [index, file] of list.entries()) {
      try {
        const response = await api.upload(file);
        uploadedAttachments.value.push(response.attachment);
        uploaded.push({ attachment: response.attachment, file });
      } catch (error) {
        const message = readableError(error);
        failed.push({ file, error: message });
        toast.error(`${file.name}：${message}`);
      } finally {
        onProgress?.(index + 1, list.length);
      }
    }
    return { uploaded, failed };
  }

  function removeUploadedAttachment(id: string) {
    uploadedAttachments.value = uploadedAttachments.value.filter((item) => item.id !== id);
  }

  function restoreUploadedAttachments(ids: string[]) {
    uploadedAttachments.value = ids
      .map((id) => attachments.value.find((item) => item.id === id))
      .filter((item): item is Attachment => Boolean(item));
  }

  async function downloadAttachment(attachment: Attachment) {
    try {
      const blob = await api.attachmentBlob(attachment.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  function mergeById<T extends { id: string; updated_at?: string; created_at?: string }>(existing: T[], incoming: T[], sortField: "updated_at" | "created_at") {
    const merged = new Map(existing.map((item) => [item.id, item]));
    incoming.forEach((item) => merged.set(item.id, item));
    return [...merged.values()].sort((a, b) => String(b[sortField] || "").localeCompare(String(a[sortField] || "")));
  }

  function replaceById<T extends { id: string }>(rows: T[], replacement: T) {
    return rows.map((row) => row.id === replacement.id ? replacement : row);
  }

  function activeRows<T extends { status?: string }>(rows: T[]) {
    return rows.filter((item) => item.status !== "deleted");
  }

  async function syncOneSchedule(schedule: Schedule) {
    const updates = await syncAndroidSchedules([schedule]).catch(() => []);
    await applyNativeScheduleUpdates(updates);
  }

  async function applyNativeScheduleUpdates(updates: Array<{ id: string; android_calendar_event_id?: string }>) {
    let changed = false;
    for (const update of updates) {
      if (!update.android_calendar_event_id) continue;
      const current = schedules.value.find((item) => item.id === update.id);
      if (current?.android_calendar_event_id === update.android_calendar_event_id) continue;
      const response = await api.patchSchedule(update.id, { android_calendar_event_id: update.android_calendar_event_id } as Partial<Schedule>);
      schedules.value = mergeById(schedules.value, [response.schedule], "updated_at");
      changed = true;
    }
    const currentChannel = channelStore.currentChannel;
    if (changed && currentChannel && channelCache.value[currentChannel.id]) {
      channelCache.value[currentChannel.id] = {
        ...channelCache.value[currentChannel.id],
        attachments: attachments.value,
        schedules: schedules.value
      };
    }
  }

  return {
    messages,
    attachments,
    todos,
    reminders,
    schedules,
    projects,
    todayData,
    searchResults,
    activeProject,
    syncStatus,
    lastSyncAt,
    lastSyncCount,
    lastSyncError,
    loading,
    offlineReadOnly,
    needsSync,
    detail,
    onlyCurrentChannel,
    uploadedAttachments,
    pendingMessages,
    pendingCount,
    searchResultCount,
    refreshAll,
    clearLocal,
    restoreOfflineSnapshot,
    applyChannelCache,
    sendMessage,
    undoMessage,
    openMessage,
    closeMessage,
    saveDetail,
    detailAction,
    attachDetailProject,
    removeProjectMessage,
    deleteAi,
    retryAi,
    deleteCurrentMessage,
    loadToday,
    patchTodo,
    createTodo,
    patchReminder,
    createManualReminder,
    createSchedule,
    patchSchedule,
    deleteSchedule,
    runSearch,
    reloadCurrentView,
    createProject,
    openProject,
    patchProject,
    deleteProject,
    downloadExport,
    clearAccountData,
    uploadFiles,
    removeUploadedAttachment,
    restoreUploadedAttachments,
    downloadAttachment
  };
});
